import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Stethoscope, Loader2, AlertTriangle, Send, ChevronRight } from "lucide-react";
import ReferralModal from "./ReferralModal";
import ResultFilingModal from "./ResultFilingModal";

const SEVERITY_COLOR = { mild: "#4ADE80", moderate: "#FBBF24", severe: "#F87171" };

/**
 * Symptom Intake card — the "How are you feeling today?" primitive.
 * Renders on the patient dashboard. Submits a new encounter with structured
 * symptoms and routes to the encounter detail once created.
 */
export default function SymptomIntakeCard({ defaultSpecialty = "general", onCreated }) {
  const nav = useNavigate();
  const [catalog, setCatalog] = useState({});
  const [specialty, setSpecialty] = useState(defaultSpecialty);
  const [complaint, setComplaint] = useState("");
  const [symptoms, setSymptoms] = useState(new Set());
  const [selfSeverity, setSelfSeverity] = useState(3);
  const [durationDays, setDurationDays] = useState("");
  const [trigger, setTrigger] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/encounters/reference/symptoms");
        setCatalog(data.catalog || {});
      } catch { /* soft-fail; still renders */ }
    })();
  }, []);

  function toggleSymptom(s) {
    const next = new Set(symptoms);
    next.has(s) ? next.delete(s) : next.add(s);
    setSymptoms(next);
  }

  async function submit(e) {
    e.preventDefault();
    if (!complaint.trim()) { toast.error("Add your main concern"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/encounters", {
        specialty, chief_complaint: complaint,
        symptoms: Array.from(symptoms),
        self_severity: selfSeverity,
        duration_days: durationDays ? Number(durationDays) : null,
        trigger: trigger || null,
      });
      toast.success(`Check-in sent · your team will review · severity ${data.severity}`);
      setComplaint(""); setSymptoms(new Set()); setSelfSeverity(3);
      setDurationDays(""); setTrigger("");
      onCreated?.(data);
      nav(`/encounters/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send check-in");
    } finally { setBusy(false); }
  }

  const options = catalog[specialty] || catalog.general || [];
  const sevColor = selfSeverity >= 8 ? "#F87171" : selfSeverity >= 5 ? "#FBBF24" : "#4ADE80";

  return (
    <form onSubmit={submit} className="ros-card space-y-4" data-testid="symptom-intake-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-[#4ADE80]/10 border border-[#4ADE80]/30 flex items-center justify-center">
          <Stethoscope className="text-[#4ADE80]" size={18} />
        </div>
        <div className="flex-1">
          <div className="font-heading text-lg leading-tight">How are you feeling today?</div>
          <div className="text-xs text-[#A3ACA7]">A quick check-in routes to your specialist within hours.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Team</span>
          <select value={specialty} onChange={(e) => { setSpecialty(e.target.value); setSymptoms(new Set()); }}
                  data-testid="intake-specialty" className="ros-input">
            {Object.keys(catalog).map((s) => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5 md:col-span-2">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Main concern (in your words)</span>
          <input value={complaint} onChange={(e) => setComplaint(e.target.value)}
                 data-testid="intake-complaint" className="ros-input" required
                 placeholder="e.g. sharp chest pain since waking up" />
        </label>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-2">Any of these?</div>
        <div className="flex flex-wrap gap-2">
          {options.map((s) => {
            const on = symptoms.has(s);
            return (
              <button type="button" key={s} onClick={() => toggleSymptom(s)}
                      data-testid={`intake-sym-${s.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        on ? "bg-[#4ADE80] text-black border-[#4ADE80]"
                           : "bg-[#1C201E] text-[#A3ACA7] border-[#272E2B] hover:text-white"
                      }`}>
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
            Severity <span style={{ color: sevColor }}>{selfSeverity}/10</span>
          </span>
          <input type="range" min={1} max={10} value={selfSeverity}
                 onChange={(e) => setSelfSeverity(Number(e.target.value))}
                 data-testid="intake-severity" className="ros-input p-0" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Days feeling this way</span>
          <input type="number" min={0} value={durationDays}
                 onChange={(e) => setDurationDays(e.target.value)}
                 data-testid="intake-duration" className="ros-input" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">What triggered it? (optional)</span>
          <input value={trigger} onChange={(e) => setTrigger(e.target.value)}
                 data-testid="intake-trigger" className="ros-input" placeholder="e.g. after exercise" />
        </label>
      </div>

      {selfSeverity >= 8 && (
        <div className="rounded-2xl p-3 flex items-center gap-2 text-sm"
             style={{ background: "#F8717118", border: "1px solid #F8717140", color: "#F87171" }}
             data-testid="intake-severe-warn">
          <AlertTriangle size={16} />
          At severity 8+ we alert your care team immediately. If this is a life-threatening emergency, call 911 / 999 / 112.
        </div>
      )}

      <button type="submit" disabled={busy} data-testid="intake-submit"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#4ADE80] text-black font-semibold disabled:opacity-50 hover:brightness-110 transition-all">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        {busy ? "Sending…" : "Send check-in →"}
      </button>
    </form>
  );
}


/* ============ Encounter Workspace ============ */
export function EncounterWorkspace({ encounterId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState("");
  const [plan, setPlan] = useState("");
  const [severity, setSeverity] = useState("mild");
  const [saving, setSaving] = useState(false);
  const [showRxModal, setShowRxModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showDxModal, setShowDxModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [resultOrder, setResultOrder] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/encounters/${encounterId}`);
        setData(data);
        setAssessment(data.encounter.assessment || "");
        setPlan(data.encounter.plan || "");
        setSeverity(data.encounter.severity || "mild");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load encounter");
      } finally { setLoading(false); }
    })();
  }, [encounterId]);

  async function updateStatus(newStatus) {
    setSaving(true);
    try {
      const { data: updated } = await api.patch(`/encounters/${encounterId}`, {
        assessment, plan, severity, status: newStatus,
      });
      toast.success(`Encounter ${newStatus}`);
      setData({ ...data, encounter: updated });
      if (newStatus === "closed") nav("/provider");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  }

  if (loading) return <div className="ros-card"><Loader2 className="animate-spin" size={18} /></div>;
  if (!data) return <div className="ros-card">Encounter not found.</div>;

  const enc = data.encounter;
  const ctx = data.context;
  const sevColor = SEVERITY_COLOR[severity] || "#A3ACA7";

  return (
    <div className="space-y-4" data-testid="encounter-workspace">
      {/* Severity banner */}
      <div className="ros-card flex items-center justify-between"
           style={{ borderColor: `${sevColor}60`, background: `${sevColor}0A` }}
           data-testid={`enc-banner-${severity}`}>
        <div>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: sevColor }}>
            {severity} · {enc.specialty}
          </div>
          <div className="font-heading text-xl leading-tight mt-0.5">{enc.chief_complaint}</div>
          <div className="text-xs text-[#A3ACA7] mt-1">
            Opened {enc.created_at?.slice(0, 10)} · Follow-up {enc.next_followup} · status {enc.status}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {["mild", "moderate", "severe"].map((s) => (
            <button key={s} onClick={() => setSeverity(s)}
                    data-testid={`enc-severity-${s}`}
                    className={`text-xs px-3 py-1 rounded-full font-bold transition-colors ${
                      severity === s ? "text-black" : "bg-[#1C201E] text-[#A3ACA7] hover:text-white"
                    }`}
                    style={severity === s ? { background: SEVERITY_COLOR[s] } : {}}>
              {s.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Zone A — Presentation */}
        <div className="ros-card space-y-2" data-testid="enc-zone-a">
          <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Zone A · Presentation</div>
          <div className="text-sm text-white leading-relaxed">{enc.chief_complaint}</div>
          <div className="flex flex-wrap gap-1.5">
            {(enc.symptoms || []).map((s) => (
              <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-[#1C201E] text-[#A3ACA7]">{s}</span>
            ))}
          </div>
          <div className="text-xs text-[#A3ACA7]">
            Self-severity <strong>{enc.self_severity}/10</strong>
            {enc.duration_days != null && <> · {enc.duration_days} days</>}
            {enc.trigger && <> · trigger: {enc.trigger}</>}
          </div>
          {enc.patient_notes && (
            <div className="text-xs text-[#7A8480] italic">&ldquo;{enc.patient_notes}&rdquo;</div>
          )}
        </div>

        {/* Zone B — Context */}
        <div className="ros-card space-y-2" data-testid="enc-zone-b">
          <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Zone B · Context</div>
          {ctx.latest_vitals && (
            <div className="text-xs">
              <span className="text-[#A3ACA7]">Vitals ({ctx.latest_vitals.date}):</span>{" "}
              <span className="text-white">
                {ctx.latest_vitals.systolic_bp}/{ctx.latest_vitals.diastolic_bp} mmHg ·
                HR {ctx.latest_vitals.heart_rate ?? "—"}
              </span>
            </div>
          )}
          {ctx.latest_egfr && (
            <div className="text-xs">
              <span className="text-[#A3ACA7]">Latest eGFR:</span>{" "}
              <span className="text-white">{ctx.latest_egfr.egfr} mL/min/1.73m²</span>
            </div>
          )}
          <div className="text-xs">
            <span className="text-[#A3ACA7]">Meds ({ctx.medications.length}):</span>{" "}
            <span className="text-white">
              {ctx.medications.slice(0, 3).map((m) => m.name).join(" · ") || "none"}
              {ctx.medications.length > 3 && ` +${ctx.medications.length - 3}`}
            </span>
          </div>
          {ctx.alerts.length > 0 && (
            <div className="text-xs">
              <span className="text-[#F87171]">Active alerts ({ctx.alerts.length}):</span>{" "}
              <span className="text-white">
                {ctx.alerts.slice(0, 2).map((a) => a.insight?.title).join(" · ")}
              </span>
            </div>
          )}
          {(ctx.orders || []).length > 0 && (
            <div className="space-y-1.5 pt-2 border-t border-[#272E2B]"
                 data-testid="enc-orders-list">
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
                Orders on this encounter
              </div>
              {ctx.orders.map((o) => {
                const isResulted = o.status === "resulted";
                const hasAbnormal = (o.results || []).some((r) =>
                  ["low", "high", "abnormal", "critical"].includes(r.flag));
                return (
                  <div key={o.id} className="flex items-center justify-between gap-2 text-xs"
                       data-testid={`enc-order-${o.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-white truncate">
                        {(o.tests || []).map((t) => t.code).join(", ")}
                      </div>
                      <div className="text-[10px] text-[#7A8480]">
                        {o.urgency} · {o.status}
                        {hasAbnormal && <span className="text-[#F87171]"> · abnormal</span>}
                      </div>
                    </div>
                    <button onClick={() => setResultOrder(o)}
                            data-testid={`enc-file-result-${o.id}`}
                            className="text-[10px] px-2.5 py-1 rounded-full font-bold"
                            style={{
                              background: isResulted ? "#1C201E" : "#4ADE80",
                              color: isResulted ? "#A3ACA7" : "#000",
                            }}>
                      {isResulted ? "View / edit results" : "File results"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Zone C — AI Assessment (rule-based v1) */}
        <div className="ros-card space-y-2" data-testid="enc-zone-c">
          <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Zone C · AI Assessment</div>
          <div className="text-sm text-white">
            AI-classified severity: <strong style={{ color: SEVERITY_COLOR[enc.ai_severity] }}>{enc.ai_severity}</strong>
            {enc.severity !== enc.ai_severity && <> · doctor override: <strong style={{ color: sevColor }}>{enc.severity}</strong></>}
          </div>
          <div className="text-xs text-[#A3ACA7]">
            {enc.ai_severity === "severe"
              ? "Rule triggered: self-severity ≥8 OR red-flag symptom present. Recommend urgent workup."
              : enc.ai_severity === "moderate"
              ? "Moderate presentation. Recommend structured workup + follow-up within 7 days."
              : "Mild presentation. Reassurance + prescribe + 30-day follow-up."}
          </div>
        </div>

        {/* Zone D — Decision */}
        <div className="ros-card space-y-3" data-testid="enc-zone-d">
          <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Zone D · Decision</div>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Assessment</span>
            <textarea rows={2} value={assessment}
                      onChange={(e) => setAssessment(e.target.value)}
                      data-testid="enc-assessment"
                      className="ros-input" placeholder="Your clinical impression…" />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Plan</span>
            <textarea rows={2} value={plan}
                      onChange={(e) => setPlan(e.target.value)}
                      data-testid="enc-plan"
                      className="ros-input" placeholder="Rx, tests, follow-up…" />
          </label>
          <div className="flex flex-wrap gap-2 pt-1">
            <button onClick={() => setShowRxModal(true)} disabled={saving}
                    data-testid="enc-close"
                    className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-[#4ADE80] text-black font-semibold disabled:opacity-50 hover:brightness-110">
              📝 Prescribe & close
            </button>
            <button onClick={() => setShowOrderModal(true)} disabled={saving}
                    data-testid="enc-order-labs"
                    className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-[#FBBF24] text-black font-semibold disabled:opacity-50 hover:brightness-110">
              🔬 Order investigation
            </button>
            <button onClick={() => setShowDxModal(true)} disabled={saving}
                    data-testid="enc-add-diagnosis"
                    className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-[#38BDF8] text-black font-semibold disabled:opacity-50 hover:brightness-110">
              🧬 Add diagnosis
            </button>
            <button onClick={() => setShowReferralModal(true)} disabled={saving}
                    data-testid="enc-refer"
                    className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-[#8B5CF6] text-white font-semibold disabled:opacity-50 hover:brightness-110">
              ↗ Refer to specialty
            </button>
            <button onClick={() => updateStatus("open")} disabled={saving}
                    data-testid="enc-save-draft"
                    className="text-sm px-4 py-2 rounded-full bg-[#1C201E] text-white disabled:opacity-50">
              Save draft
            </button>
          </div>
        </div>
      </div>
      {showRxModal && (
        <RxPrescribeModal
          encounter={enc}
          currentAssessment={assessment}
          currentPlan={plan}
          currentSeverity={severity}
          onClose={() => setShowRxModal(false)}
          onComplete={(closedEnc) => {
            setData({ ...data, encounter: closedEnc });
            setShowRxModal(false);
            toast.success("Encounter closed with prescriptions");
            nav("/provider");
          }}
        />
      )}
      {showOrderModal && (
        <OrderInvestigationModal
          encounter={enc}
          currentAssessment={assessment}
          currentPlan={plan}
          currentSeverity={severity}
          onClose={() => setShowOrderModal(false)}
          onComplete={(updated) => {
            setData({ ...data, encounter: updated });
            setShowOrderModal(false);
            toast.success("Tests ordered · encounter moved to awaiting-diagnostics");
            nav("/encounters");
          }}
        />
      )}
      {showDxModal && (
        <DiagnosisPickerModal
          encounter={enc}
          onClose={() => setShowDxModal(false)}
          onSaved={(diagnoses) => {
            setData({ ...data, encounter: { ...enc, diagnoses } });
            setShowDxModal(false);
            toast.success(`Added ${diagnoses.length} diagnosis code(s)`);
          }}
        />
      )}
      {showReferralModal && (
        <ReferralModal
          encounter={enc}
          patient={data?.patient || { id: enc.patient_id }}
          onClose={() => setShowReferralModal(false)}
          onSaved={() => {
            setShowReferralModal(false);
            toast.success("Referral sent · target doctor will see it in their inbox");
          }}
        />
      )}
      {resultOrder && (
        <ResultFilingModal
          order={resultOrder}
          onClose={() => setResultOrder(null)}
          onSaved={() => {
            setResultOrder(null);
            toast.success("Results filed. Ordering doctor notified.");
            // Re-fetch encounter so orders list reflects the "resulted" status
            api.get(`/encounters/${enc.id}`).then(({ data: d }) => setData(d));
          }}
        />
      )}
    </div>
  );
}


/* ============ Rx Prescribe Modal — formulary + follow-up scheduler ============ */
function RxPrescribeModal({ encounter, currentAssessment, currentPlan, currentSeverity, onClose, onComplete }) {
  const [drugs, setDrugs] = useState([]);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([{ generic_name: "", dose: "", frequency: "Daily", indication: "" }]);
  const [followup, setFollowup] = useState(encounter.next_followup || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/formulary/all");
        setDrugs(data.drugs || []);
      } catch { /* soft-fail */ }
    })();
  }, []);

  const filtered = drugs.filter((d) => {
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    return d.generic_name.toLowerCase().includes(n) ||
           (d.category || "").toLowerCase().includes(n) ||
           (d.specialty || "").toLowerCase().includes(n);
  }).slice(0, 20);

  function pickDrug(drug, rowIdx) {
    const next = [...rows];
    next[rowIdx] = { ...next[rowIdx], generic_name: drug.generic_name,
                     indication: drug.indications || "" };
    setRows(next);
  }
  function updateRow(idx, key, val) {
    const next = [...rows]; next[idx] = { ...next[idx], [key]: val }; setRows(next);
  }
  function addRow() {
    setRows([...rows, { generic_name: "", dose: "", frequency: "Daily", indication: "" }]);
  }
  function removeRow(idx) {
    if (rows.length === 1) return;
    setRows(rows.filter((_, i) => i !== idx));
  }

  async function prescribeAndClose() {
    const valid = rows.filter((r) => r.generic_name && r.dose);
    setSaving(true);
    try {
      // Prescribe each valid row
      const prescriptionIds = [];
      for (const r of valid) {
        try {
          const { data } = await api.post(
            `/provider/prescribe?patient_id=${encounter.patient_id}`,
            { generic_name: r.generic_name, dose: r.dose,
              frequency: r.frequency, indication: r.indication });
          if (data?.medication?.id) prescriptionIds.push(data.medication.id);
        } catch (err) {
          toast.error(`Failed to prescribe ${r.generic_name}: ${err?.response?.data?.detail || "error"}`);
        }
      }
      // Close encounter with the plan + follow-up + prescription refs
      const planText = currentPlan
        ? `${currentPlan}\n\nRx: ${valid.map((r) => `${r.generic_name} ${r.dose} ${r.frequency}`).join(" · ")}`
        : `Rx: ${valid.map((r) => `${r.generic_name} ${r.dose} ${r.frequency}`).join(" · ")}`;
      const { data: updated } = await api.patch(`/encounters/${encounter.id}`, {
        assessment: currentAssessment,
        plan: planText,
        severity: currentSeverity,
        status: "closed",
        next_followup: followup,
        prescription_ids: prescriptionIds,
      });
      onComplete(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to close encounter");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
         onClick={onClose} data-testid="rx-modal">
      <div className="bg-[#0F1312] border border-[#272E2B] rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 space-y-5"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading text-xl">Prescribe & close encounter</div>
            <div className="text-xs text-[#A3ACA7] mt-0.5">
              Pick meds from the cross-specialty formulary and set the follow-up date.
            </div>
          </div>
          <button onClick={onClose} data-testid="rx-modal-close"
                  className="text-[#A3ACA7] hover:text-white text-xl leading-none">✕</button>
        </div>

        {/* Formulary search + rows */}
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-1">
              Formulary search ({drugs.length} drugs across all specialties)
            </div>
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="rx-search" className="ros-input"
                   placeholder="Search by name, class, or specialty (e.g. metformin, statin, cardiology)" />
            {q && (
              <div className="mt-1 max-h-40 overflow-y-auto border border-[#272E2B] rounded-xl"
                   data-testid="rx-search-results">
                {filtered.map((d) => (
                  <button key={`${d.specialty}-${d.generic_name}`}
                          type="button"
                          onClick={() => { pickDrug(d, rows.length - 1); setQ(""); }}
                          data-testid={`rx-pick-${d.generic_name.replace(/\s+/g, "-").toLowerCase()}`}
                          className="w-full text-left px-3 py-2 hover:bg-[#1C201E] border-t border-[#272E2B] first:border-t-0 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white">{d.generic_name}</span>
                      <span className="text-[10px] text-[#7A8480] uppercase tracking-wider">{d.specialty}</span>
                    </div>
                    <div className="text-[10px] text-[#A3ACA7]">{d.category} · {d.indications}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2" data-testid="rx-rows">
            {rows.map((r, i) => (
              <div key={i} className="ros-card grid grid-cols-1 md:grid-cols-12 gap-2 items-center"
                   data-testid={`rx-row-${i}`}>
                <input value={r.generic_name}
                       onChange={(e) => updateRow(i, "generic_name", e.target.value)}
                       placeholder="Drug (or search above)"
                       data-testid={`rx-name-${i}`}
                       className="ros-input md:col-span-4" />
                <input value={r.dose} onChange={(e) => updateRow(i, "dose", e.target.value)}
                       placeholder="Dose (e.g. 500 mg)"
                       data-testid={`rx-dose-${i}`}
                       className="ros-input md:col-span-2" />
                <select value={r.frequency}
                        onChange={(e) => updateRow(i, "frequency", e.target.value)}
                        data-testid={`rx-freq-${i}`}
                        className="ros-input md:col-span-2">
                  <option>Once</option><option>Daily</option><option>BID</option>
                  <option>TID</option><option>QID</option><option>Weekly</option>
                  <option>PRN</option>
                </select>
                <input value={r.indication}
                       onChange={(e) => updateRow(i, "indication", e.target.value)}
                       placeholder="Indication"
                       data-testid={`rx-ind-${i}`}
                       className="ros-input md:col-span-3" />
                <button type="button" onClick={() => removeRow(i)}
                        data-testid={`rx-remove-${i}`}
                        className="text-[#A3ACA7] hover:text-[#F87171] md:col-span-1 text-lg">✕</button>
              </div>
            ))}
            <button type="button" onClick={addRow} data-testid="rx-add-row"
                    className="text-xs text-[#4ADE80] hover:text-white flex items-center gap-1">
              + Add another medication
            </button>
          </div>
        </div>

        {/* Follow-up scheduler */}
        <div className="ros-card space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Follow-up</div>
          <div className="flex items-center gap-3">
            <input type="date" value={followup}
                   onChange={(e) => setFollowup(e.target.value)}
                   data-testid="rx-followup-date" className="ros-input" />
            <div className="flex gap-1.5">
              {[
                ["+3 d", 3], ["+7 d", 7], ["+2 wk", 14], ["+1 mo", 30], ["+3 mo", 90],
              ].map(([label, days]) => (
                <button key={days} type="button"
                        onClick={() => {
                          const d = new Date();
                          d.setDate(d.getDate() + days);
                          setFollowup(d.toISOString().slice(0, 10));
                        }}
                        data-testid={`rx-fu-${days}`}
                        className="text-xs px-2.5 py-1 rounded-full bg-[#1C201E] text-[#A3ACA7] hover:text-white">
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#272E2B]">
          <button onClick={onClose} data-testid="rx-cancel"
                  className="text-sm px-4 py-2 rounded-full bg-[#1C201E] text-white">
            Cancel
          </button>
          <button onClick={prescribeAndClose} disabled={saving}
                  data-testid="rx-confirm"
                  className="flex items-center gap-1.5 text-sm px-5 py-2 rounded-full bg-[#4ADE80] text-black font-semibold disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            {saving ? "Saving…" : "Prescribe & close encounter"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============ Order Investigation Modal — lab / imaging picker ============ */
function OrderInvestigationModal({ encounter, currentAssessment, currentPlan, currentSeverity, onClose, onComplete }) {
  const [catalog, setCatalog] = useState({});
  const [panels, setPanels] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [urgency, setUrgency] = useState("routine");
  const [notes, setNotes] = useState("");
  const [customTest, setCustomTest] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/encounters/reference/tests");
        setCatalog(data.catalog || {});
        setPanels(data.panels || {});
      } catch { /* soft-fail */ }
    })();
  }, []);

  function toggle(code) {
    const next = new Set(selected);
    next.has(code) ? next.delete(code) : next.add(code);
    setSelected(next);
  }
  function loadPanel() {
    const codes = panels[encounter.specialty] || [];
    if (!codes.length) { toast.error("No default panel for this specialty"); return; }
    setSelected(new Set([...selected, ...codes]));
    toast.success(`Loaded ${codes.length}-test ${encounter.specialty} panel`);
  }
  function addCustom() {
    const t = customTest.trim();
    if (!t) return;
    const code = `CUSTOM:${t}`;
    const next = new Set(selected);
    next.add(code);
    setSelected(next);
    setCustomTest("");
  }

  async function orderAndFile() {
    if (selected.size === 0) {
      toast.error("Pick at least one test");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/encounters/${encounter.id}/orders`, {
        tests: Array.from(selected),
        urgency,
        clinical_notes: notes || null,
      });
      // Also patch the encounter with the current assessment + plan (so notes carry)
      const { data: updated } = await api.patch(`/encounters/${encounter.id}`, {
        assessment: currentAssessment,
        plan: currentPlan || `Ordered ${selected.size} test(s) · ${urgency}`,
        severity: currentSeverity,
        status: "awaiting_labs",
      });
      onComplete(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to place order");
    } finally { setSaving(false); }
  }

  const totalTests = Object.values(catalog).flat().length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
         onClick={onClose} data-testid="order-modal">
      <div className="bg-[#0F1312] border border-[#272E2B] rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 space-y-5"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading text-xl">Order investigation</div>
            <div className="text-xs text-[#A3ACA7] mt-0.5">
              Pick tests from the {totalTests}-test panel. Encounter will move to &ldquo;Awaiting diagnostics&rdquo;.
            </div>
          </div>
          <button onClick={onClose} data-testid="order-modal-close"
                  className="text-[#A3ACA7] hover:text-white text-xl leading-none">✕</button>
        </div>

        {/* Urgency + specialty panel shortcut */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Urgency</span>
          {["routine", "urgent", "stat"].map((u) => (
            <button key={u} onClick={() => setUrgency(u)} type="button"
                    data-testid={`order-urgency-${u}`}
                    className={`text-xs px-3 py-1 rounded-full font-bold ${
                      urgency === u ? "text-black" : "bg-[#1C201E] text-[#A3ACA7] hover:text-white"
                    }`}
                    style={urgency === u
                      ? { background: u === "stat" ? "#F87171" : u === "urgent" ? "#FBBF24" : "#4ADE80" }
                      : {}}>
              {u.toUpperCase()}
            </button>
          ))}
          {panels[encounter.specialty] && (
            <button type="button" onClick={loadPanel}
                    data-testid="order-panel-shortcut"
                    className="ml-auto text-xs px-3 py-1 rounded-full bg-[#38BDF8]/15 text-[#38BDF8] hover:bg-[#38BDF8]/25 font-semibold">
              ⚡ Load {encounter.specialty} panel ({panels[encounter.specialty].length} tests)
            </button>
          )}
        </div>

        {/* Test picker by category */}
        <div className="space-y-3 max-h-96 overflow-y-auto pr-2" data-testid="order-picker">
          {Object.entries(catalog).map(([cat, tests]) => (
            <div key={cat}>
              <div className="text-[10px] uppercase tracking-wider text-[#7A8480] mb-1.5">{cat}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {tests.map((t) => {
                  const on = selected.has(t.code);
                  return (
                    <button key={t.code} type="button" onClick={() => toggle(t.code)}
                            data-testid={`order-test-${t.code}`}
                            className={`flex items-center justify-between text-left px-3 py-2 rounded-xl border transition-colors text-sm ${
                              on ? "bg-[#4ADE80]/15 border-[#4ADE80] text-white"
                                 : "bg-[#1C201E] border-[#272E2B] text-[#A3ACA7] hover:text-white"
                            }`}>
                      <span>{t.name}</span>
                      <span className="text-[10px] text-[#7A8480] ml-2 whitespace-nowrap">
                        ~{t.turnaround_h}h · {t.code}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Clinical notes */}
        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Clinical notes (for the lab)</span>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid="order-notes"
                    className="ros-input" placeholder="Rule out X, correlate with Y…" />
        </label>

        {/* Custom test entry — manual escape hatch when the catalog is missing a test */}
        <div className="ros-card space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
            Custom test (add if missing from catalog)
          </div>
          <div className="flex gap-2">
            <input value={customTest}
                   onChange={(e) => setCustomTest(e.target.value)}
                   data-testid="order-custom-test"
                   className="ros-input flex-1"
                   placeholder="e.g. Anti-Jo-1 antibody" />
            <button type="button" onClick={addCustom}
                    data-testid="order-custom-add"
                    className="text-xs px-3 py-2 rounded-full bg-[#1C201E] text-[#4ADE80] hover:text-white">
              + Add
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-[#272E2B]">
          <div className="text-xs text-[#A3ACA7]" data-testid="order-count">
            {selected.size} test(s) selected
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} data-testid="order-cancel"
                    className="text-sm px-4 py-2 rounded-full bg-[#1C201E] text-white">
              Cancel
            </button>
            <button onClick={orderAndFile} disabled={saving || selected.size === 0}
                    data-testid="order-confirm"
                    className="flex items-center gap-1.5 text-sm px-5 py-2 rounded-full bg-[#FBBF24] text-black font-semibold disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {saving ? "Ordering…" : `🔬 Place order (${selected.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ============ Diagnosis Picker (ICD-10) ============ */
function DiagnosisPickerModal({ encounter, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [customTitle, setCustomTitle] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/encounters/reference/diagnoses", {
          params: { specialty: encounter.specialty, search: q },
        });
        if (!cancel) setResults(data.results || []);
      } catch { /* soft-fail */ }
    })();
    return () => { cancel = true; };
  }, [q, encounter.specialty]);

  function toggle(dx) {
    setSelected((prev) => {
      const exists = prev.find((p) => p.code === dx.code);
      return exists ? prev.filter((p) => p.code !== dx.code) : [...prev, dx];
    });
  }
  function addCustom() {
    const t = customTitle.trim();
    if (!t) return;
    setSelected((prev) => [...prev, { code: "custom", title: t, category: "Manual" }]);
    setCustomTitle("");
  }
  async function save() {
    if (selected.length === 0) { toast.error("Pick at least one diagnosis"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/encounters/${encounter.id}/diagnoses`,
        { diagnoses: selected });
      onSaved(data.diagnoses || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save diagnoses");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
         onClick={onClose} data-testid="dx-modal">
      <div className="bg-[#0F1312] border border-[#272E2B] rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 space-y-4"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading text-xl">Add diagnosis (ICD-10)</div>
            <div className="text-xs text-[#A3ACA7] mt-0.5">
              Pick from the {encounter.specialty} catalog or add a custom diagnosis.
              Diagnoses are visible to the whole care team.
            </div>
          </div>
          <button onClick={onClose} data-testid="dx-modal-close"
                  className="text-[#A3ACA7] hover:text-white text-xl leading-none">✕</button>
        </div>

        <input value={q} onChange={(e) => setQ(e.target.value)}
               data-testid="dx-search"
               className="ros-input" placeholder="Search by code, title, or category…" />

        <div className="max-h-60 overflow-y-auto border border-[#272E2B] rounded-xl"
             data-testid="dx-results">
          {results.map((r) => {
            const on = !!selected.find((s) => s.code === r.code);
            return (
              <button key={`${r.specialty}-${r.code}`} type="button"
                      onClick={() => toggle(r)}
                      data-testid={`dx-pick-${r.code.replace(/\./g, "-")}`}
                      className={`w-full text-left px-3 py-2 border-t border-[#272E2B] first:border-t-0 text-sm transition-colors ${
                        on ? "bg-[#38BDF8]/10 text-white"
                           : "hover:bg-[#1C201E] text-[#A3ACA7]"
                      }`}>
                <div className="flex items-center justify-between">
                  <span>
                    <span className="font-bold text-[#38BDF8]">{r.code}</span>
                    <span className="ml-2 text-white">{r.title}</span>
                  </span>
                  <span className="text-[10px] text-[#7A8480] uppercase tracking-wider">
                    {r.specialty} · {r.category}
                  </span>
                </div>
              </button>
            );
          })}
          {results.length === 0 && (
            <div className="text-xs text-[#7A8480] p-3">No matching diagnoses.</div>
          )}
        </div>

        {/* Manual entry */}
        <div className="ros-card space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
            Custom diagnosis (manual entry — when catalog lacks the code)
          </div>
          <div className="flex gap-2">
            <input value={customTitle}
                   onChange={(e) => setCustomTitle(e.target.value)}
                   data-testid="dx-custom-title"
                   className="ros-input flex-1"
                   placeholder="Free-text diagnosis title" />
            <button type="button" onClick={addCustom}
                    data-testid="dx-custom-add"
                    className="text-xs px-3 py-2 rounded-full bg-[#1C201E] text-[#4ADE80] hover:text-white">
              + Add
            </button>
          </div>
        </div>

        {selected.length > 0 && (
          <div className="space-y-1" data-testid="dx-selected">
            <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
              Selected ({selected.length})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {selected.map((s, i) => (
                <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#38BDF8]/15 text-[#38BDF8]"
                      data-testid={`dx-chip-${i}`}>
                  {s.code}: {s.title}
                  <button onClick={() => toggle(s)} className="ml-2 text-[#7A8480] hover:text-white">
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#272E2B]">
          <button onClick={onClose} data-testid="dx-cancel"
                  className="text-sm px-4 py-2 rounded-full bg-[#1C201E] text-white">
            Cancel
          </button>
          <button onClick={save} disabled={saving || selected.length === 0}
                  data-testid="dx-confirm"
                  className="flex items-center gap-1.5 text-sm px-5 py-2 rounded-full bg-[#38BDF8] text-black font-semibold disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            Save {selected.length} diagnosis{selected.length === 1 ? "" : "es"}
          </button>
        </div>
      </div>
    </div>
  );
}



export function EncountersPage() {
  const [buckets, setBuckets] = useState({ awaiting_review: [], awaiting_labs: [], followup_due: [] });
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/encounters");
        setBuckets(data.buckets);
      } finally { setLoading(false); }
    })();
    // Referrals inbox (silently skipped for patients)
    api.get("/referrals/inbox?status=open")
      .then(({ data }) => setReferrals(data.referrals || []))
      .catch(() => {});
  }, []);

  if (loading) return <div className="ros-card"><Loader2 className="animate-spin" size={18} /></div>;

  return (
    <div className="space-y-6" data-testid="encounters-page">
      <div className="ros-card">
        <div className="font-heading text-xl">Encounters</div>
        <div className="text-xs text-[#A3ACA7] mt-1">
          Awaiting review · awaiting diagnostics · follow-up due.
        </div>
      </div>
      {referrals.length > 0 && <ReferralInboxStrip referrals={referrals} setReferrals={setReferrals} nav={nav} />}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { key: "awaiting_review", label: "Awaiting review", color: "#F87171" },
          { key: "awaiting_labs",   label: "Awaiting diagnostics", color: "#FBBF24" },
          { key: "followup_due",    label: "Follow-up due", color: "#4ADE80" },
        ].map((col) => (
          <div key={col.key} data-testid={`enc-col-${col.key}`}>
            <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: col.color }}>
              {col.label} ({(buckets[col.key] || []).length})
            </div>
            <div className="space-y-2">
              {(buckets[col.key] || []).length === 0 ? (
                <div className="ros-card text-xs text-[#7A8480]">Nothing here.</div>
              ) : buckets[col.key].map((e) => (
                <button key={e.id} onClick={() => nav(`/encounters/${e.id}`)}
                        data-testid={`enc-row-${e.id}`}
                        className="ros-card w-full text-left hover:bg-[#1C201E] transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-wider"
                           style={{ color: SEVERITY_COLOR[e.severity] }}>
                        {e.severity} · {e.specialty}
                      </div>
                      <div className="text-sm text-white mt-1 leading-tight">{e.chief_complaint}</div>
                      <div className="text-[10px] text-[#A3ACA7] mt-1">
                        {e.created_at?.slice(0, 10)} · {(e.symptoms || []).length} symptoms
                      </div>
                    </div>
                    <ChevronRight size={14} className="text-[#A3ACA7] mt-1" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



/* ============ Referral Inbox Strip — shown on top of the provider inbox ============ */
function ReferralInboxStrip({ referrals, setReferrals, nav }) {
  const [busy, setBusy] = useState({});

  async function act(refId, next) {
    setBusy({ ...busy, [refId]: true });
    try {
      await api.patch(`/referrals/${refId}`, { status: next });
      setReferrals((prev) => prev.filter((r) => r.id !== refId));
      toast.success(`Referral ${next}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update referral");
    } finally { setBusy({ ...busy, [refId]: false }); }
  }

  const URG = { routine: "#4ADE80", urgent: "#FBBF24", stat: "#F87171" };

  return (
    <div className="ros-card space-y-3" data-testid="ref-inbox">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-heading text-lg">Incoming referrals</div>
          <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
            Handoffs from other doctors — pending acceptance
          </div>
        </div>
        <span className="text-[10px] px-2 py-1 rounded-full bg-[#8B5CF6]/15 text-[#8B5CF6] font-bold">
          {referrals.length} PENDING
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {referrals.map((r) => (
          <div key={r.id} className="rounded-2xl border p-3 space-y-2"
               style={{ borderColor: `${URG[r.urgency] || "#272E2B"}80` }}
               data-testid={`ref-inbox-row-${r.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                    style={{ color: URG[r.urgency], background: `${URG[r.urgency]}18` }}>
                {r.urgency.toUpperCase()}
              </span>
              <span className="text-[10px] text-[#7A8480]">
                From {r.from_doctor_name} · {r.from_specialty}
              </span>
            </div>
            <div className="text-sm text-white leading-tight">{r.reason}</div>
            <div className="text-xs text-[#A3ACA7] line-clamp-3">{r.clinical_summary}</div>
            {(r.relevant_diagnoses || []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {r.relevant_diagnoses.slice(0, 4).map((d, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[#38BDF8]/15 text-[#38BDF8]">
                    {d.code}
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <button onClick={() => act(r.id, "accepted")}
                      disabled={!!busy[r.id]}
                      data-testid={`ref-accept-${r.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-[#4ADE80] text-black font-semibold disabled:opacity-50">
                Accept
              </button>
              <button onClick={() => act(r.id, "declined")}
                      disabled={!!busy[r.id]}
                      data-testid={`ref-decline-${r.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-[#1C201E] text-[#F87171] disabled:opacity-50">
                Decline
              </button>
              {r.encounter_id && (
                <button onClick={() => nav(`/encounters/${r.encounter_id}`)}
                        data-testid={`ref-open-encounter-${r.id}`}
                        className="ml-auto text-[10px] text-[#7A8480] hover:text-white flex items-center gap-1">
                  Open encounter <ChevronRight size={10} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
